import React, { useEffect, useState } from "react";
import { supabase } from "../config/supabaseClient";
import { useAuth } from "../config/AuthContext";
import { Table, Button, Modal, Input, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

const STUDENT_FUNCTION = "create-student";

const AddStudent = () => {
  const { profile } = useAuth();
  const isAdmin = profile?.type === "admin";

  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const fetchStudents = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, name, email")
      .eq("type", "student")
      .order("created_at", { ascending: true });
    if (!error) {
      setUsers(
        data.map((row, index) => ({ id: row.id, number: index + 1, ...row }))
      );
    }
  };

  useEffect(() => {
    fetchStudents();

    const channel = supabase
      .channel("profiles-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles" },
        () => fetchStudents()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const addUser = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      message.warning("Please enter name, email, and password");
      return;
    }
    if (!isAdmin) {
      message.warning("You are not authorized to add users");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke(STUDENT_FUNCTION, {
        body: { action: "create", name, email, password },
      });

      if (error) {
        message.error(error.message || "Failed to add user");
        return;
      }

      message.success("User added successfully");
      setIsModalOpen(false);
      setName("");
      setEmail("");
      setPassword("");
    } catch (error) {
      console.error("Error adding user:", error);
      message.error("Failed to add user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user) => {
    setEditingUserId(user.id);
    setName(user.name || "");
    setEmail(user.email || "");
    setIsEditModalOpen(true);
  };

  const editUser = async () => {
    if (!name.trim() || !email.trim()) {
      message.warning("Please enter name and email");
      return;
    }
    if (!isAdmin) {
      message.warning("You are not authorized to edit users");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ name, email })
        .eq("id", editingUserId);
      if (error) {
        message.error(error.message);
        return;
      }
      message.success("User updated successfully");
      setIsEditModalOpen(false);
      setName("");
      setEmail("");
      setEditingUserId(null);
    } catch (error) {
      console.error("Error editing user:", error);
      message.error("Failed to edit user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!isAdmin) {
      message.warning("You are not authorized to delete users");
      return;
    }

    try {
      const { error } = await supabase.functions.invoke(STUDENT_FUNCTION, {
        body: { action: "delete", userId },
      });
      if (error) {
        message.error(error.message || "Failed to delete user");
        return;
      }
      message.success("User deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error("Failed to delete user. Please try again.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "number", key: "number" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Actions",
      key: "actions",
      render: (_, user) => (
        <div className="flex">
          <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(user)}>
            Edit
          </Button>
          <Button type="link" icon={<DeleteOutlined />} danger onClick={() => deleteUser(user.id)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 md:p-1 pt-10">
      <div className="flex justify-between items-center bg-gray-900 shadow p-4 rounded">
        <h2 className="text-xl font-semibold text-white">Student Registration</h2>
        <Button
          className="bg-green-600 font-semibold text-white hover:bg-green-700"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Add User
        </Button>
      </div>

      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 6 }}
        className="bg-white rounded-lg"
      />

      <Modal
        title="Add New User"
        visible={isModalOpen}
        onOk={addUser}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={loading}
        okText="Add"
        cancelText="Cancel"
      >
        <Input
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3"
        />
        <Input
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-3"
        />
        <Input.Password
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Modal>

      <Modal
        title="Edit User"
        visible={isEditModalOpen}
        onOk={editUser}
        onCancel={() => setIsEditModalOpen(false)}
        confirmLoading={loading}
        okText="Update"
        cancelText="Cancel"
      >
        <Input
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3"
        />
        <Input
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-3"
        />
      </Modal>
    </div>
  );
};

export default AddStudent;
